/**
 * usePaginationParams Hook
 * Manages pagination state in URL search params for persistence and shareability
 */

import { useSearchParams } from 'react-router-dom';
import { useCallback, useMemo, useEffect, useRef } from 'react';

export interface PaginationParams {
  page: number;
  limit: number;
  search: string;
  sort: string;
  [key: string]: string | number; // Additional filter params
}

interface UsePaginationParamsOptions {
  defaultLimit?: number;
  defaultSort?: string;
  allowedLimits?: number[];
  scrollToTop?: boolean; // Scroll to top when page changes (for full pages, not modals)
}

const DEFAULT_OPTIONS: UsePaginationParamsOptions = {
  defaultLimit: 25,
  defaultSort: 'last_name:asc',
  allowedLimits: [10, 25, 50, 100],
};

export const usePaginationParams = (options: UsePaginationParamsOptions = {}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { defaultLimit, defaultSort, allowedLimits, scrollToTop } = { ...DEFAULT_OPTIONS, ...options };
  const isFirstRender = useRef(true);

  // Parse current params from URL
  const params = useMemo((): PaginationParams => {
    const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10));
    let limit = parseInt(searchParams.get('limit') || String(defaultLimit), 10);

    // Normalize limit to allowed values
    if (!allowedLimits?.includes(limit)) {
      limit = defaultLimit!;
    }

    const search = searchParams.get('search') || '';
    const sort = searchParams.get('sort') || defaultSort!;

    // Collect all other params as filters
    const filters: Record<string, string | number> = {};
    searchParams.forEach((value, key) => {
      if (!['page', 'limit', 'search', 'sort'].includes(key)) {
        // Try to parse as number if it looks like one
        filters[key] = /^\d+$/.test(value) ? parseInt(value, 10) : value;
      }
    });

    return { page, limit, search, sort, ...filters };
  }, [searchParams, defaultLimit, defaultSort, allowedLimits]);

  // Scroll to top of page when page changes (skip on initial render)
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (scrollToTop) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [params.page, scrollToTop]);

  // Update a single param (resets page to 1 for search/filter changes)
  const setParam = useCallback(
    (key: string, value: string | number | null) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);

        if (value === null || value === '') {
          next.delete(key);
        } else {
          next.set(key, String(value));
        }

        // Reset to page 1 when changing search, sort, or filters (not when changing page itself)
        if (key !== 'page' && key !== 'limit') {
          next.set('page', '1');
        }

        // Reset to page 1 when changing limit
        if (key === 'limit') {
          next.set('page', '1');
        }

        return next;
      });
    },
    [setSearchParams]
  );

  // Convenience setters
  const setPage = useCallback((page: number) => setParam('page', page), [setParam]);
  const setLimit = useCallback((limit: number) => setParam('limit', limit), [setParam]);
  const setSearch = useCallback((search: string) => setParam('search', search || null), [setParam]);
  const setSort = useCallback((sort: string) => setParam('sort', sort), [setParam]);

  // Set a filter param
  const setFilter = useCallback(
    (key: string, value: string | number | null) => {
      setParam(key, value);
    },
    [setParam]
  );

  // Clear all filters (keep pagination defaults)
  const clearFilters = useCallback(() => {
    setSearchParams((prev) => {
      const next = new URLSearchParams();
      // Keep only page and limit if they were set
      const page = prev.get('page');
      const limit = prev.get('limit');
      if (page) next.set('page', '1'); // Reset to page 1
      if (limit) next.set('limit', limit);
      return next;
    });
  }, [setSearchParams]);

  // Correct page if it exceeds total pages (call this after receiving API response)
  const correctPageIfNeeded = useCallback(
    (totalPages: number) => {
      if (params.page > totalPages && totalPages > 0) {
        setPage(totalPages);
      }
    },
    [params.page, setPage]
  );

  return {
    params,
    setPage,
    setLimit,
    setSearch,
    setSort,
    setFilter,
    clearFilters,
    correctPageIfNeeded,
  };
};
